import type { SpotlightRect } from './tourGeometry';

export interface ArrowPoint {
  x: number;
  y: number;
}

export interface ArrowPath {
  /** SVG path `d` attribute value. */
  path: string;
  /** Point on the source rectangle border. */
  start: ArrowPoint;
  /** Point on the target rectangle border. */
  end: ArrowPoint;
  /** Angle from start to end, in radians. */
  angle: number;
}

export type TourArrowPlacement = 'above' | 'below' | 'left' | 'right' | 'center';

export interface TourArrowLayout {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  placement: TourArrowPlacement;
}

export interface TourArrowGeometry {
  calloutLayout: TourArrowLayout;
  arrowPath?: ArrowPath;
}

export function computeTourArrowGeometry(
  calloutRect: SpotlightRect,
  targetRect: SpotlightRect
): TourArrowGeometry {
  const layout: TourArrowLayout = {
    top: calloutRect.top,
    left: calloutRect.left,
    width: calloutRect.width,
    maxHeight: calloutRect.height,
    placement: 'center',
  };

  return {
    calloutLayout: layout,
    arrowPath: rectsOverlap(calloutRect, targetRect) ? undefined : computeArrowPath(calloutRect, targetRect),
  };
}

function intersectRectBorder(from: ArrowPoint, to: ArrowPoint, rect: SpotlightRect): ArrowPoint {
  const dx = to.x - from.x;
  const dy = to.y - from.y;

  if (dx === 0 && dy === 0) {
    return { x: from.x, y: from.y };
  }

  const left = rect.left;
  const right = rect.left + rect.width;
  const top = rect.top;
  const bottom = rect.top + rect.height;

  let tMin = Infinity;

  if (dx > 0) {
    tMin = Math.min(tMin, (right - from.x) / dx);
  } else if (dx < 0) {
    tMin = Math.min(tMin, (left - from.x) / dx);
  }

  if (dy > 0) {
    tMin = Math.min(tMin, (bottom - from.y) / dy);
  } else if (dy < 0) {
    tMin = Math.min(tMin, (top - from.y) / dy);
  }

  if (!Number.isFinite(tMin) || tMin < 0) {
    return { x: from.x, y: from.y };
  }

  return {
    x: from.x + tMin * dx,
    y: from.y + tMin * dy,
  };
}

function rectCenter(rect: SpotlightRect): ArrowPoint {
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

/**
 * Computes a straight SVG arrow from the border of `sourceRect` to the border
 * of `targetRect`. The line connects the closest facing edges by shooting a ray
 * from each rectangle's center toward the other.
 */
export function computeArrowPath(sourceRect: SpotlightRect, targetRect: SpotlightRect): ArrowPath {
  const sourceCenter = rectCenter(sourceRect);
  const targetCenter = rectCenter(targetRect);

  const start = intersectRectBorder(sourceCenter, targetCenter, sourceRect);
  const end = intersectRectBorder(targetCenter, sourceCenter, targetRect);
  const angle = Math.atan2(end.y - start.y, end.x - start.x);

  return {
    path: `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} L ${end.x.toFixed(2)} ${end.y.toFixed(2)}`,
    start,
    end,
    angle,
  };
}

/**
 * Returns true when two rectangles overlap by more than a small threshold,
 * meaning an arrow would be confusing or hidden.
 */
export function rectsOverlap(rectA: SpotlightRect, rectB: SpotlightRect): boolean {
  const overlapLeft = Math.max(rectA.left, rectB.left);
  const overlapTop = Math.max(rectA.top, rectB.top);
  const overlapRight = Math.min(rectA.left + rectA.width, rectB.left + rectB.width);
  const overlapBottom = Math.min(rectA.top + rectA.height, rectB.top + rectB.height);

  return overlapRight > overlapLeft && overlapBottom > overlapTop;
}
